/* ═══════════════════════════════════════════════════════════════
   AuthService — Real JWT auth via EC2 API

   Handles sign-in, registration, token persistence, and session
   management. Tokens are stored in Electron's appData via IPC.
   ═══════════════════════════════════════════════════════════════ */

import type { AppModule } from '../../shared/modules';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  firmId: string;
  firmName: string;
  firmSlug: string;
  teamMemberId?: string;
  roleId?: string;
  role: 'site_admin' | 'admin' | 'manager' | 'member';
  permissions: string[];
  canAccessAdmin: boolean;
  canAccessTeam: boolean;
  canAccessInvoices: boolean;
  canAccessAllProjects: boolean;
  canAccessBilling: boolean;
  canManageSpecs: boolean;
  canEditProjects: boolean;
  canDeleteProjects: boolean;
  canDeleteClients: boolean;
  enabledModules: AppModule[];
  emailVerified?: boolean;
}

import { AUTH_BASE as AUTH_URL } from '../lib/apiBase';

/** In-memory JWT token (also persisted via IPC) */
let currentToken: string | null = null;

/** Auth state change listeners */
type AuthListener = (user: AuthUser | null) => void;
const listeners: Set<AuthListener> = new Set();

function notifyListeners(user: AuthUser | null) {
  listeners.forEach((cb) => cb(user));
}

async function authFetch(endpoint: string, body: Record<string, any>): Promise<any> {
  const response = await fetch(`${AUTH_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({ error: response.statusText }));
  if (!response.ok) {
    throw new Error(data.error || `Auth error: ${response.status}`);
  }
  return data;
}

async function authGet(endpoint: string): Promise<any> {
  if (!currentToken) throw new Error('Not authenticated');
  const response = await fetch(`${AUTH_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentToken}`,
    },
  });

  const data = await response.json().catch(() => ({ error: response.statusText }));
  if (!response.ok) {
    throw new Error(data.error || `Auth error: ${response.status}`);
  }
  return data;
}

async function authPostWithToken(endpoint: string, body: Record<string, any>): Promise<any> {
  if (!currentToken) throw new Error('Not authenticated');
  const response = await fetch(`${AUTH_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentToken}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({ error: response.statusText }));
  if (!response.ok) {
    throw new Error(data.error || `Auth error: ${response.status}`);
  }
  return data;
}

/** Save token both in-memory and to disk via IPC */
async function persistToken(token: string) {
  currentToken = token;
  if (window.api?.saveAuthToken) {
    await window.api.saveAuthToken(token);
  }
}

/** Get the current JWT token (for apiClient to use) */
export function getAuthToken(): string | null {
  return currentToken;
}

export const AuthService = {
  /** Sign in with email + password */
  signIn: async (
    email: string,
    password: string
  ): Promise<{ user: AuthUser | null; error: string | null }> => {
    try {
      const data = await authFetch('/auth/login', { email, password });
      await persistToken(data.token);
      notifyListeners(data.user);
      // Fire-and-forget activity log — token is set above so the
      // logger can stamp this user; never await, never throw.
      try {
        const { logLogin } = await import('./activityLogger');
        logLogin(data.user?.teamMemberId || data.user?.id);
      } catch {
        /* ignore — activity logging is best-effort */
      }
      return { user: data.user, error: null };
    } catch (err: any) {
      return { user: null, error: err.message };
    }
  },

  /** Exchange a Clerk session token for a SpecForma session (docs/AUTH_CLERK_PLAN.md) */
  signInWithClerk: async (
    clerkToken: string
  ): Promise<{ user: AuthUser | null; error: string | null }> => {
    try {
      const data = await authFetch('/auth/clerk-exchange', { token: clerkToken });
      await persistToken(data.token);
      notifyListeners(data.user);
      try {
        const { logLogin } = await import('./activityLogger');
        logLogin(data.user?.teamMemberId || data.user?.id);
      } catch {
        /* best-effort */
      }
      return { user: data.user, error: null };
    } catch (err: any) {
      return { user: null, error: err.message };
    }
  },

  /** Adopt an already-minted SpecForma JWT (desktop deep-link handoff). */
  adoptToken: async (token: string): Promise<{ user: AuthUser | null; error: string | null }> => {
    try {
      currentToken = token.trim();
      const data = await authGet('/auth/me');
      if (!data.user) throw new Error('Invalid session token');
      await persistToken(currentToken);
      notifyListeners(data.user);
      return { user: data.user, error: null };
    } catch (err: any) {
      currentToken = null;
      return { user: null, error: err.message || 'Invalid session token' };
    }
  },

  /** Sign out — clear token and notify listeners */
  signOut: async () => {
    // Log BEFORE clearing the token so the request is authenticated.
    try {
      const { logLogout } = await import('./activityLogger');
      await logLogout();
    } catch {
      /* ignore */
    }
    currentToken = null;
    if (window.api?.clearAuthToken) {
      await window.api.clearAuthToken();
    }
    // Web: also end the Clerk session, or the root auto-auth bridge would
    // sign us straight back in. clerk-js exposes window.Clerk once loaded;
    // desktop never loads it (browser handoff), so this is a no-op there.
    try {
      await (window as any).Clerk?.signOut?.();
    } catch {
      /* best-effort */
    }
    notifyListeners(null);
  },

  /** Load user from saved token (call on app startup) */
  getUser: async (): Promise<AuthUser | null> => {
    // Try loading saved token from disk
    if (!currentToken && window.api?.loadAuthToken) {
      const saved = await window.api.loadAuthToken();
      if (saved) currentToken = saved;
    }
    if (!currentToken) return null;

    try {
      const data = await authGet('/auth/me');
      return data.user;
    } catch {
      // Token expired or invalid — clear it
      currentToken = null;
      if (window.api?.clearAuthToken) await window.api.clearAuthToken();
      return null;
    }
  },

  /** Subscribe to auth state changes */
  onAuthChange: (callback: AuthListener) => {
    listeners.add(callback);
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            listeners.delete(callback);
          },
        },
      },
    };
  },

  /** Register a new firm + admin user */
  signUpAndCreateFirm: async (
    email: string,
    password: string,
    displayName: string,
    firmName: string,
    firmSlug: string
  ): Promise<{ user: AuthUser | null; error: string | null }> => {
    try {
      const data = await authFetch('/auth/register-firm', {
        email,
        password,
        displayName,
        firmName,
        firmSlug,
      });
      await persistToken(data.token);
      notifyListeners(data.user);
      return { user: data.user, error: null };
    } catch (err: any) {
      return { user: null, error: err.message };
    }
  },

  /** Join an existing firm by company code */
  signUpAndJoinFirm: async (
    email: string,
    password: string,
    displayName: string,
    firmCode: string
  ): Promise<{ user: AuthUser | null; error: string | null }> => {
    try {
      const data = await authFetch('/auth/register-join', {
        email,
        password,
        displayName,
        firmCode,
      });
      await persistToken(data.token);
      notifyListeners(data.user);
      return { user: data.user, error: null };
    } catch (err: any) {
      return { user: null, error: err.message };
    }
  },

  /** Change password (requires current password) */
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      await authPostWithToken('/auth/change-password', { currentPassword, newPassword });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /** Request a password reset code (sent to email) */
  forgotPassword: async (email: string): Promise<{ success: boolean; error: string | null }> => {
    try {
      await authFetch('/auth/forgot-password', { email });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /** Reset password using 6-digit code */
  resetPassword: async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      await authFetch('/auth/reset-password', { email, code, newPassword });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /** Check if a firm slug is available */
  checkSlug: async (_slug: string): Promise<boolean> => {
    // TODO: Add server endpoint if needed. For now assume available.
    return true;
  },

  /** Trigger a fresh verification code send for the given email (resend). */
  requestVerification: async (
    email: string
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      await authFetch('/auth/request-verification', { email });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  /** Submit the 6-digit verification code to mark email_verified = true. */
  verifyEmail: async (
    email: string,
    code: string
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      await authFetch('/auth/verify-email', { email, code });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },
};
